import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

const pad2 = (n) => String(n).padStart(2, '0');

const parseDateInput = (value) => {
  if (!value) return null;
  if (typeof value === 'string') {
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

// App-wide standard: all dates render as MM/DD/YYYY.
export const formatDate = (value) => {
  const d = parseDateInput(value);
  if (!d) return '—';
  return `${pad2(d.getMonth() + 1)}/${pad2(d.getDate())}/${d.getFullYear()}`;
};

// App-wide standard: all date-times render as MM/DD/YYYY hh:mm AM/PM.
export const formatDateTime = (value) => {
  const d = parseDateInput(value);
  if (!d) return '—';
  const hours = d.getHours();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  return `${formatDate(d)} ${hours % 12 || 12}:${pad2(d.getMinutes())} ${ampm}`;
};

export const formatTime = (timeString) => {
  // Null-guard: P22+ claims may have null time_in/time_out because the
  // Rhinobill UI dropped those fields. Return em-dash placeholder so
  // the table renders cleanly instead of crashing.
  if (!timeString || typeof timeString !== 'string') return '—';
  // Idempotency-guard: legacy claims store the time as "9:00 AM" with
  // the suffix already in place. Without this check we'd re-process and
  // emit "9:00 AM AM" (visible double-suffix bug pre-existing P22).
  if (/\s?(AM|PM)$/i.test(timeString.trim())) return timeString.trim();
  const parts = timeString.split(':');
  if (parts.length < 2) return timeString;
  const [hours, minutes] = parts;
  const hour = parseInt(hours, 10);
  if (Number.isNaN(hour)) return timeString;
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

export const getStatusColor = (status) => {
  const colors = {
    Draft: 'status-draft',
    Pending: 'status-pending',
    Submitted: 'status-submitted',
    Accepted: 'status-accepted',
    Paid: 'status-paid',
    Rejected: 'status-rejected',
    Voided: 'status-voided',
  };
  return colors[status] || 'status-draft';
};

export const calculateDuration = (timeIn, timeOut) => {
  const [inHours, inMins] = timeIn.split(':').map(Number);
  const [outHours, outMins] = timeOut.split(':').map(Number);
  
  let totalInMins = inHours * 60 + inMins;
  let totalOutMins = outHours * 60 + outMins;
  
  if (totalOutMins < totalInMins) {
    totalOutMins += 24 * 60;
  }
  
  const durationMins = totalOutMins - totalInMins;
  const hours = Math.floor(durationMins / 60);
  const mins = durationMins % 60;
  
  return { hours, mins, totalMins: durationMins };
};
